import { API_BASE_URL, ApiError, api, tryRefreshToken } from './client'
import { parseEInvoiceObservation } from './einvoice-observation'
import type { EInvoiceObservation } from './einvoice-observation'
import type { InvoiceBuyerInput } from './sales-order-api'
import type { components } from './generated/schema'
import type { SkipReason } from '@/lib/schemas/einvoice-resolution-schemas'
import { trackApiError } from '@/lib/recent-api-errors'

export type EInvoiceStatus =
    | 'publishing'
    | 'publish_unknown'
    | 'publish_duplicated'
    | 'publish_failed'
    | 'draft'
    | 'pushed'
    | 'pending'
    | 'created'
    | 'issuing'
    | 'issued'
    | 'rejected'
    | 'failed'
    | 'manual_issued'
    | 'skipped'

/** Who closed an order by hand, from the newest closing event. Present on reads
 *  of a manual_issued or skipped invoice, null otherwise. */
export interface EInvoiceClosure {
    action: 'manual_issued' | 'skipped'
    at: string
    actor_username: string
    actor_full_name: string
    /** Skip reason code; empty for a manual issue. */
    reason: string
    note: string
}

export interface EInvoiceDraftDrift {
    order_total: number
    lines_total: number
    difference: number
}

/** What MISA keeps for the order's RefID: an unnumbered draft or nothing. */
export interface EInvoiceMisaState {
    misa_draft_exists: boolean
    misa_draft_created_at?: string
}

export interface EInvoice {
    id: string
    sales_order_id: string
    observation?: EInvoiceObservation | null
    draft_revision?: string | null
    provider?: string
    ref_id?: string
    reference_code?: string
    tracking_code?: string
    inv_series?: string
    transaction_id?: string
    inv_no?: string
    status: EInvoiceStatus
    invoice_number?: string
    pdf_url?: string
    xml_url?: string
    error_message?: string
    retry_count: number
    issued_at?: string
    pushed_at?: string
    publication_requested_at?: string
    /** Chỉ có trong response phát hành: ngày hóa đơn đã được dời sang ngày
     *  phát hành (giờ VN) vì MISA từ chối hóa đơn có ngày cũ hơn hóa đơn đã
     *  phát hành gần nhất của ký hiệu. Ngày giao hàng của đơn không đổi. */
    invoice_date_adjusted_to?: string
    closure?: EInvoiceClosure | null
    /** Set on reads of a draft that failed on DRAFT_TOTAL_DRIFT with the numbers
     *  kept: order total, lines + VAT, and lines minus order. */
    draft_drift?: EInvoiceDraftDrift
    stale?: boolean
    amount_mismatch?: boolean
    push_held?: boolean
    reconcile_miss_count?: number
    created_at: string
    updated_at: string
}

export type EInvoiceDraftItem = components['schemas']['EInvoiceDraftLine']
export type EInvoiceDraftContent = components['schemas']['EInvoiceDraftContent']

export interface EInvoiceDraftInput {
    revision: string
    invoice_date: string
    items: Pick<
        EInvoiceDraftItem,
        'line_number' | 'item_name' | 'discount_percent' | 'vat_amount'
    >[]
    removed_line_numbers?: number[]
}
export interface EInvoiceDraftReset {
    reset_from_order: true
    revision: string
}
export type RefreshEInvoiceDraft = (
    buyer?: InvoiceBuyerInput,
    draft?: EInvoiceDraftInput,
    reset?: EInvoiceDraftReset,
) => Promise<unknown>

export type EInvoiceResolutionSource = 'pending_list' | 'order_detail'

/** How the series and number were chosen: taken as suggested, taken then
 *  changed, or typed from scratch. */
export type ManualSuggestionUse = 'picked' | 'picked_edited' | 'typed'

export interface ManualIssueInput {
    inv_series: string
    inv_no: string
    /** YYYY-MM-DD, a Vietnam calendar day. */
    issued_date: string
    delete_old_draft: boolean
    source: EInvoiceResolutionSource
    suggestion_used: ManualSuggestionUse
}

/** An invoice made on MISA's website that no ERP order owns yet. */
export interface ManualCandidate {
    /** Empty when the series cannot be told; the person types it. */
    inv_series: string
    inv_no: string
    /** YYYY-MM-DD, a Vietnam calendar day; empty when MISA's date was unreadable. */
    inv_date: string
    total_amount: number
    created_at: string
}

export interface ManualCandidates {
    items: ManualCandidate[]
    warning?: string
}

export interface SkipInput {
    reason: SkipReason
    note: string
    delete_old_draft: boolean
    source: EInvoiceResolutionSource
}

export interface EInvoiceTemplate {
    ip_template_id: string
    inv_template_no: string
    inv_series: string
    template_name: string
    is_more_vat_rate: boolean
    invoice_with_code: boolean
}

export interface EInvoiceUsage {
    remaining: number
}

export interface EInvoiceDocument {
    observation: EInvoiceObservation | null
    previewToken: string | null
    blob: Blob
    source: string | null
    status: string | null
}

export const einvoiceApi = {
    document: async (
        orderId: string,
        signal: AbortSignal,
    ): Promise<EInvoiceDocument> => {
        const path = `/einvoices/sales-orders/${encodeURIComponent(orderId)}/document`
        const load = () =>
            fetch(`${API_BASE_URL}${path}`, {
                credentials: 'include',
                signal,
            }).catch((error: unknown) => {
                if (!signal.aborted) {
                    trackApiError({
                        status: 0,
                        code: 'NETWORK_ERROR',
                        method: 'GET',
                        path,
                        message: error instanceof Error ? error.message : '',
                    })
                }
                throw error
            })
        let response = await load()
        if (
            response.status === 401 &&
            !signal.aborted &&
            (await tryRefreshToken())
        ) {
            signal.throwIfAborted()
            response = await load()
        }
        signal.throwIfAborted()
        const observation = parseEInvoiceObservation(
            response.headers.get('X-EInvoice-Observation'),
            orderId,
        )
        if (!response.ok) {
            const body = await response.json().catch(() => null)
            signal.throwIfAborted()
            const error = Object.assign(
                new ApiError(
                    response.status,
                    body?.error?.code ?? 'DOCUMENT_UNAVAILABLE',
                    body?.error?.message ?? '',
                    undefined,
                    response.headers.get('X-Request-ID') ?? undefined,
                ),
                { observation },
            )
            trackApiError({
                requestId: error.requestId,
                status: error.status,
                code: error.code,
                method: 'GET',
                path,
                message: error.message,
            })
            throw error
        }
        if (
            !response.headers
                .get('Content-Type')
                ?.toLowerCase()
                .includes('application/pdf')
        ) {
            trackApiError({
                requestId: response.headers.get('X-Request-ID') ?? undefined,
                status: response.status,
                code: 'INVALID_DOCUMENT',
                method: 'GET',
                path,
                message: 'INVALID_DOCUMENT',
            })
            throw Object.assign(new Error('INVALID_DOCUMENT'), { observation })
        }
        return {
            observation,
            previewToken: response.headers.get('X-EInvoice-Preview-Token'),
            blob: await response.blob(),
            source: response.headers.get('X-EInvoice-Source'),
            status: response.headers.get('X-EInvoice-Status'),
        }
    },
    // draft saves a reviewable row without contacting the provider.
    draft: (orderId: string) =>
        api.post<EInvoice>(`/einvoices/sales-orders/${orderId}/draft`),
    getDraftEditor: (orderId: string) =>
        api.get<EInvoiceDraftContent>(
            `/einvoices/sales-orders/${orderId}/draft/editor`,
        ),
    refreshDraft: (
        orderId: string,
        buyer?: InvoiceBuyerInput,
        draft?: EInvoiceDraftInput,
        reset?: EInvoiceDraftReset,
    ) =>
        api.post<EInvoice>(
            `/einvoices/sales-orders/${orderId}/draft/regenerate`,
            reset ??
                (buyer || draft ? { invoice_buyer: buyer, draft } : undefined),
        ),
    publish: (
        orderId: string,
        previewToken: string,
        notIssuedByHand?: boolean,
    ) =>
        api.post<EInvoice>(`/einvoices/sales-orders/${orderId}/publish`, {
            preview_token: previewToken,
            confirmed: true,
            ...(notIssuedByHand ? { confirmed_not_issued_manually: true } : {}),
        }),
    // M-167: "Bán ngay" có tích hóa đơn — server tự tạo nháp và phát hành.
    publishQuickSale: (orderId: string) =>
        api.post<EInvoice>(
            `/einvoices/sales-orders/${orderId}/quick-sale-publish`,
        ),
    push: (orderId: string) =>
        api.post<EInvoice>(`/einvoices/sales-orders/${orderId}/draft/push`),
    withdraw: (orderId: string) =>
        api.delete<EInvoice>(`/einvoices/sales-orders/${orderId}/draft`),
    refresh: (orderId: string) =>
        api.post<EInvoice>(`/einvoices/sales-orders/${orderId}/refresh`),
    misaState: (orderId: string) =>
        api.get<EInvoiceMisaState>(
            `/einvoices/sales-orders/${orderId}/misa-state`,
        ),
    // Closing an order by hand: the response carries no `data` when the order
    // goes back to having no invoice row (reopen of a bare marker).
    manualIssue: (orderId: string, input: ManualIssueInput) =>
        api.post<EInvoice>(
            `/einvoices/sales-orders/${orderId}/manual-issue`,
            input,
        ),
    manualCandidates: (orderId: string) =>
        api.get<ManualCandidates>(
            `/einvoices/sales-orders/${orderId}/manual-candidates`,
        ),
    skip: (orderId: string, input: SkipInput) =>
        api.post<EInvoice>(`/einvoices/sales-orders/${orderId}/skip`, input),
    reopen: (orderId: string, source: EInvoiceResolutionSource) =>
        api.post<EInvoice>(`/einvoices/sales-orders/${orderId}/reopen`, {
            source,
        }),
    syncTemplates: () =>
        api.post<EInvoiceTemplate[]>('/einvoices/templates/sync'),
    get: (orderId: string) =>
        // No invoice row yet (not requested, or the worker is still creating
        // it) is data, not a failure.
        api.get<EInvoice>(`/einvoices/sales-orders/${orderId}`, {
            expected: [{ status: 404, code: 'EINVOICE_NOT_FOUND' }],
        }),
    getUsage: () => api.get<EInvoiceUsage>('/einvoices/usage'),
    // pdfUrl returns the full absolute URL for the inline PDF proxy. The
    // browser loads it with cookies (withCredentials), so no token plumbing.
    pdfUrl: (orderId: string) =>
        `${API_BASE_URL}/einvoices/sales-orders/${orderId}/pdf`,
}
